// ─────────────────────────────────────────────────────────────────────────────
// MutedNotice — A MUTED CALL SAYS SO (owner ruling, Fault 2, 2026-08-31).
//
// On 2026-08-29 a call sat muted from 12:05:52 to 12:15:43. `accept_device_pcm`
// returns False on `self.muted` before audio enters the L1 ring, so nothing was
// captured, nothing reached STT, no utterance opened and no error was raised —
// the box was working exactly as designed and the call was dead. The only thing
// the interface showed was a mic icon in its other state, on a 64px button that
// renders during a session and says nothing else.
//
// The owner's ruling: a local call must not be able to sit muted with nothing
// shown. Whether he pressed it stops mattering at that point.
//
// PRESENTATIONAL ONLY — no hook, no fetch, no timer. Both values come from the
// BOX (the daemon owns the mute and restates its age on every lane heartbeat),
// so this component cannot drift from the microphone it describes, and cannot
// invent a duration when the box has not stated one.
//
// PIXEL: placement and styling here are the minimum that makes the state
// unmissable on the two surfaces that had nothing. The dimensional language and
// final placement are yours.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';

export function formatStanding(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
}

export default function MutedNotice(
  { muted, standingS, compact }: { muted: boolean; standingS: number; compact?: boolean },
) {
  if (!muted) return null;
  // The duration is STATED ONLY WHEN THE BOX HAS STATED IT. 0 is the value on
  // the mute edge itself — "muted for 0s" would be a reading, and there isn't
  // one yet.
  const held = standingS > 0 ? ` · ${formatStanding(standingS)}` : '';
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        letterSpacing: '.06em',
        wordBreak: 'break-word',
        color: '#f59e0b',
        fontSize: compact ? 10 : 11,
        fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
        textAlign: 'center',
        padding: compact ? 0 : '4px 8px',
        borderRadius: compact ? 0 : 6,
        background: compact ? undefined : 'rgba(245,158,11,.10)',
        border: compact ? undefined : '1px solid rgba(245,158,11,.35)',
        boxShadow: compact
          ? undefined
          : '0 4px 12px rgba(0,0,0,.45), inset 0 1px 0 rgba(255,255,255,.06)',
      }}
    >
      MIC MUTED{held} · SHE CANNOT HEAR YOU
    </div>
  );
}
